import {
  addLocationAgent,
  addLocationAgentToService,
  deleteLocationAgent,
  deleteLocationAgentFromService,
  getAgentDetails,
  getLocationAgents,
  getLocationAgentsEarningPlans,
  reportError,
  SuccessToast,
  updateLocationAgentShares,
} from '@vgrubs/components';
import {
  LocationAgent,
  LocationAgentServiceKey,
  LocationAgentsEarningPlans,
  SaveLocationAgentShareItem,
} from 'components/src/types/api/location.types';
import { ResponseType } from 'components/src/types/shared.types';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';

// Display order + labels for the per-service sections (vOrders is shown as
// "vTablet" to match the existing Earning Plans modal).
export const AGENT_SERVICES: { key: LocationAgentServiceKey; label: string }[] =
  [
    { key: 'vGrubs', label: 'vGrubs' },
    { key: 'vOrders', label: 'vTablet' },
    { key: 'vDrive', label: 'vDrive' },
  ];

export const isAgentOnService = (
  agent: LocationAgent,
  service: LocationAgentServiceKey
) =>
  service === 'vGrubs'
    ? agent.onVGrubs
    : service === 'vDrive'
    ? agent.onVDrive
    : agent.onVOrders;

export const useLocationAgents = (
  id: string | number,
  setLoading: Dispatch<SetStateAction<boolean>>
) => {
  const [agentsData, setAgentsData] =
    useState<LocationAgentsEarningPlans | null>(null);
  // Full list of location agents (incl. agents not on any service).
  const [agents, setAgents] = useState<LocationAgent[]>([]);

  const refetch = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setLoading(true);
        const [plans, list] = await Promise.all([
          getLocationAgentsEarningPlans(id),
          getLocationAgents(id),
        ]);
        setAgentsData(plans.data.data);
        setAgents(list.data.data);
      } catch (error) {
        reportError(error);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [id, setLoading]
  );

  useEffect(() => {
    refetch(true);
  }, [refetch]);

  const runMutation = async (
    request: () => Promise<{ data: ResponseType<unknown> }>
  ) => {
    try {
      setLoading(true);
      const { data } = await request();
      SuccessToast(data.message);
      await refetch(true);
      return true;
    } catch (error) {
      reportError(error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // The picker selects a user, but POST /agents needs Agent.id — resolve it first.
  const addAgent = (userId: number) =>
    runMutation(async () => {
      const { data } = await getAgentDetails(userId);
      return addLocationAgent(id, data.data.agent.id);
    });

  const deleteAgent = (agentId: number) =>
    runMutation(() => deleteLocationAgent(id, agentId));

  const addAgentToService = (
    agentId: number,
    service: LocationAgentServiceKey
  ) => runMutation(() => addLocationAgentToService(id, agentId, service));

  const removeAgentFromService = (
    agentId: number,
    service: LocationAgentServiceKey
  ) => runMutation(() => deleteLocationAgentFromService(id, agentId, service));

  const saveShares = (agentShares: SaveLocationAgentShareItem[]) =>
    runMutation(() => updateLocationAgentShares(id, { agents: agentShares }));

  return {
    agentsData,
    agents,
    refetch,
    addAgent,
    deleteAgent,
    addAgentToService,
    removeAgentFromService,
    saveShares,
  };
};
